import { DataIngestionServiceAPI } from '@API/data-services/data-ingestion-service'
import { RootState } from '@AppState/redux-store'
import { LoadingRows } from '@Components/util/loading-rows'
import {
  DataImport,
  DataImportRowChangeType,
  DataImportStatus,
} from '@Generated/back-office-client/src'
import {
  Badge,
  Card,
  ChevronDownIcon,
  Flex,
  Heading,
  Link,
  Section,
  Skeleton,
  Table,
  Text,
} from '@radix-ui/themes'
import { useQuery } from '@tanstack/react-query'
import { Accordion as RadixAccordion } from 'radix-ui'
import { Fragment, useCallback, useState } from 'react'
import { CollapsibleTags } from '@Components/imports/collapsible-tags'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import '../common/accordion.scss'
import { Accordion } from '@Components/common/accordion'
import { JsonView } from '@Components/common/json-view'
import { ArrowLeftIcon, ChevronUpIcon } from '@radix-ui/react-icons'
import { Routes } from '@Components/main-app/types'

export const ImportHistoryDetails = () => {
  const nav = useNavigate()
  const { activeCustomerId } = useSelector((state: RootState) => state.users)
  const [selectedRowDetails, setSelectedRowDetails] = useState<string[]>([])
  const location = useLocation()
  const params = useParams()

  const importId = params?.importId
  const locationState = location.state as { dataImport?: DataImport; returnSearch?: string } | null
  const dataImport = locationState?.dataImport ?? null
  const returnSearch = locationState?.returnSearch ?? ''

  const { data: importDetails, isLoading: detailsLoading } = useQuery({
    queryKey: [`${activeCustomerId}-${importId}-import-details`],
    refetchInterval: 15000,
    queryFn: async () => {
      return DataIngestionServiceAPI.getImportDetails(importId!)
    },
    enabled: !!importId,
  })

  const { data: loadedDataImport } = useQuery({
    queryKey: [`${activeCustomerId}-${importId}-import`],
    queryFn: async () => {
      return DataIngestionServiceAPI.getImportById(importId!)
    },
    enabled: !dataImport && !!importId,
  })

  const getChangeBadge = useCallback((changeType: DataImportRowChangeType) => {
    let color
    switch (changeType) {
      case DataImportRowChangeType.NEW:
        color = 'green'
        break
      case DataImportRowChangeType.UPDATE:
        color = 'blue'
        break
      case DataImportRowChangeType.NO_CHANGE:
        color = 'gray'
        break
      case DataImportRowChangeType.ERROR:
        color = 'red'
        break
      default:
        return null
    }

    // @ts-expect-error Color type error
    return <Badge color={color}>{changeType}</Badge>
  }, [])

  const mapImportDetails = useCallback(() => {
    if (!importDetails) {
      return []
    }

    return importDetails.map((importDetail) => {
      const accordionContent = (
        <RadixAccordion.Content className="AccordionContent json-view-background">
          <JsonView data={importDetail.rawData} />
        </RadixAccordion.Content>
      )

      const isOpen = selectedRowDetails.find((r) => importDetail.id === r)

      return (
        <Fragment key={importDetail.id}>
          <Table.Row align="center" className="import-details-row">
            <Table.Cell
              align="center"
              className="cell-no-border cell-border-right"
              onClick={() => {
                const closedState = selectedRowDetails.filter((r) => importDetail.id !== r)
                setSelectedRowDetails(isOpen ? closedState : [...closedState, importDetail.id])
              }}>
              {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </Table.Cell>
            <Table.RowHeaderCell className="cell-no-border cell-border-right">
              {importDetail.id}
            </Table.RowHeaderCell>
            <Table.Cell className="cell-no-border cell-border-right">
              {getChangeBadge(importDetail.changeType)}
            </Table.Cell>
            <Table.Cell className="cell-no-border">{importDetail.error}</Table.Cell>
          </Table.Row>

          {isOpen ? (
            <Table.Row align="center">
              <Table.Cell colSpan={4}>
                <Accordion
                  items={[{ id: importDetail.id, value: importDetail.id, child: accordionContent }]}
                  values={[importDetail.id]}
                />
              </Table.Cell>
            </Table.Row>
          ) : null}
        </Fragment>
      )
    })
  }, [getChangeBadge, importDetails, selectedRowDetails])

  // Try to use the import we had context of when navigating from the list page, otherwise fall back to
  // the one loaded remotely (hit when deep linking to an import)
  const importToRender = dataImport ?? loadedDataImport

  if (!importToRender) {
    return null
  }

  return (
    <>
      <Section className="section-width" size="1">
        <Flex direction="column" gap="4">
          <Flex>
            <Link
              className="back-link"
              onClick={() => {
                const path = returnSearch ? `${Routes.IMPORTS}?${returnSearch}` : Routes.IMPORTS
                nav(path)
              }}>
              <ArrowLeftIcon className="icon" /> Back
            </Link>
          </Flex>
          <Heading>Import Details</Heading>
          <Card>
            <Flex direction="column" gap="3">
              <Heading size="4">Import Information</Heading>
              <Flex gap="2">
                <Text weight="bold">ID:</Text>
                <Text>{importToRender.id}</Text>
              </Flex>
              <Flex gap="2">
                <Text weight="bold">Status:</Text>
                <Text color={importToRender.status === DataImportStatus.COMPLETE ? 'green' : 'red'}>
                  {importToRender.status}
                </Text>
              </Flex>
              <Flex gap="2">
                <Text weight="bold">File Name:</Text>
                <Text>{importToRender.fileName}</Text>
              </Flex>
              <Flex gap="2">
                <Text weight="bold">File Type:</Text>
                <Text>{importToRender.fileType}</Text>
              </Flex>
              <Flex gap="2">
                <Text weight="bold">Started At:</Text>
                <Text>{importToRender.startedAt.toISOString()}</Text>
              </Flex>
              <Flex gap="2">
                <Text weight="bold">Finished At:</Text>
                <Text>{importToRender.finishedAt?.toISOString()}</Text>
              </Flex>
              <CollapsibleTags tags={importToRender.tags} />
              {importToRender.importMetadata && (
                <Flex direction="column" gap="2">
                  <Text weight="bold">Import Metadata:</Text>
                  <div className="json-view-background">
                    <JsonView data={importToRender.importMetadata} />
                  </div>
                </Flex>
              )}
              <Flex gap="2">
                <Text weight="bold">Message:</Text>
                <Text>{importToRender.message}</Text>
              </Flex>
            </Flex>
          </Card>
        </Flex>
      </Section>

      <Section className="section-width" size="1">
        <Skeleton loading={detailsLoading}>
          <Table.Root variant="surface">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell align="center">
                  <ChevronDownIcon />
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>ID</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Change Type</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Error</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>{detailsLoading ? <LoadingRows /> : mapImportDetails()}</Table.Body>
          </Table.Root>
        </Skeleton>
      </Section>
    </>
  )
}
